import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import {
  TextInput as BaseTextInput,
  Insets,
  PixelRatio,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Button } from './button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import { Key } from '@platform/shortcuts/utils/keyboard-platform';
import { Portal } from '@gorhom/portal';
import { ResetAndSaveButtons } from './sales-rep/reset-and-save-buttons.ios';
import { TextInput, Props as TextInputProps } from './text-input';

const fontScale = PixelRatio.getFontScale();

export const INPUT_HEIGHT_OLD = 34 * fontScale;
export const INPUT_HEIGHT = 24 * fontScale;
const INPUT_HIT_SLOP: Insets = {
  bottom: 0,
  left: 0,
  right: 0,
  top: 0,
};

const BUTTON_HIT_SLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

export type Props = Omit<TextInputProps, 'onChange' | 'onChangeText'> & {
  allowNegative?: boolean;
  allowZero?: boolean;
  buttonContainerStyle?: StyleProp<ViewStyle>;
  containerStyle?: StyleProp<ViewStyle>;
  inDraggableSheet?: boolean;
  isConfirming: boolean;
  leftElement?: React.ReactNode;
  onChange: (value: string | null) => void;
  onClear?: () => void;
  onConfirm: () => Promise<boolean>;
  portalHost?: string;
  rightElement?: React.ReactNode;
};

export const OverrideInput: React.FC<Props> = ({
  allowNegative,
  allowZero,
  buttonContainerStyle,
  containerStyle,
  editable,
  focusable,
  inDraggableSheet,
  inputAccessoryViewID,
  isConfirming,
  onChange,
  onClear,
  onConfirm,
  onKeyPress,
  portalHost,
  rightElement,
  leftElement,
  value,
  ...rest
}) => {
  const [styles] = useStyles();

  const inputRef = useRef<BaseTextInput>(null);
  const [isDirty, setIsDirty] = useState(false);

  const cancelChange = () => {
    onClear?.();
    setIsDirty(false);
    inputRef.current?.blur();
  };

  const confirmChange = async () => {
    // Validate the display value if it exists, otherwise cancel the change
    // If there is no display value, confirm with the null value
    // Can be configured to allow zeroes, but cannot be set to zero if there is no existing value.
    if (value) {
      const convertedValue = Number(value);
      if (
        isNaN(convertedValue) ||
        (!allowZero && convertedValue === 0) ||
        (!allowNegative && convertedValue < 0)
      ) {
        return cancelChange();
      }
    }

    const isSuccess = await onConfirm();

    if (isSuccess) {
      setIsDirty(false);
      inputRef.current?.blur();
    }
  };

  const customInputAccessoryViewID: string | undefined =
    Platform.OS === 'ios' ? inputAccessoryViewID : undefined;

  return (
    <>
      <TextInput
        containerStyle={StyleSheet.flatten([
          styles.container,
          styles.height,
          containerStyle,
          editable ? styles.editable : styles.disabled,
        ])}
        editable={editable}
        focusable={focusable}
        // hitSlop prevents clicks from propagating to elements underneath the input
        hitSlop={INPUT_HIT_SLOP}
        inDraggableSheet={inDraggableSheet}
        inputAccessoryViewID={customInputAccessoryViewID}
        inputContainerStyle={styles.inputContainer}
        inputRef={inputRef}
        keyboardType="decimal-pad"
        leftElement={leftElement}
        leftElementStyle={leftElement ? styles.inputLeftElement : null}
        onChangeText={(text) => {
          const nextText = text.trim();
          setIsDirty(true);

          const convertedValue = nextText ? Number(nextText) : NaN;
          if (
            (!isNaN(convertedValue) && !allowNegative && convertedValue < 0) ||
            (!allowZero && convertedValue === 0)
          ) {
            onChange(null);
          } else {
            onChange(nextText);
          }
        }}
        onKeyPress={async (e) => {
          if (!editable) {
            return;
          }

          switch (e.nativeEvent.key) {
            case Key.Enter:
              // Handle Enter locally and prevent it from bubbling to global handlers
              e.preventDefault();
              await confirmChange();
              return;
            case Key.Escape:
              // Handle Escape locally and prevent it from bubbling to global handlers
              e.preventDefault();
              cancelChange();
              return;
          }

          // Call the parent onKeyPress handler to handle web key press events for keys not handled locally
          onKeyPress?.(e);
        }}
        onSubmitEditing={async () => await confirmChange()}
        placeholder="--"
        rightElement={rightElement}
        rightElementStyle={rightElement ? styles.inputRightElement : null}
        selectTextOnFocus={editable}
        // flatten is needed to prevent the styles array from being re-rendered and
        // causing an on blur on android since it might be undefined which produces a
        // unstable reference to the style
        style={StyleSheet.flatten([TYPOGRAPHY.small, styles.input])}
        value={value}
        {...rest}
      />
      {customInputAccessoryViewID ? (
        <ResetAndSaveButtons
          cancelChange={cancelChange}
          confirmChange={confirmChange}
          customInputAccessoryViewID={customInputAccessoryViewID}
          isConfirming={isConfirming}
        />
      ) : null}
      {isDirty && Platform.OS !== 'ios' ? (
        <Portal hostName={portalHost}>
          <View style={[styles.buttons, styles.topV2, buttonContainerStyle]}>
            <Button
              disabled={isConfirming}
              focusable={false}
              hitSlop={BUTTON_HIT_SLOP}
              hoverStyle={styles.hoverStyle}
              onPress={cancelChange}
              size="xsmall"
              testID="price-override-reset-button"
              trackingData={{ name: 'price_override_input.reset_button' }}
              type="tertiary"
            >
              {i18n.t('reset')}
            </Button>
            <Button
              focusable={false}
              hitSlop={BUTTON_HIT_SLOP}
              hoverStyle={styles.hoverStyle}
              loading={isConfirming}
              onPress={confirmChange}
              size="xsmall"
              testID="price-override-confirm-button"
              trackingData={{ name: 'price_override_input.confirm_button' }}
              type="tertiary"
            >
              {i18n.t('save')}
            </Button>
          </View>
        </Portal>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  buttons: {
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    flex: 1,
    flexDirection: 'row',
    gap: 4,
    padding: 4,
    position: 'absolute',
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 2,
    }),
  },
  container: {
    borderColor: theme.colors.stroke,
    height: INPUT_HEIGHT_OLD,
    width: '80%',
  },
  disabled: {
    opacity: 0.38,
  },
  editable: {
    opacity: 1,
  },
  height: {
    borderColor: theme.colors.white,
    height: INPUT_HEIGHT,
  },
  hoverStyle: {
    backgroundColor: theme.colors.background,
  },
  input: {
    ...FONTS.primary,
    color: theme.colors.primary,
    // Reset base TextInput line height
    lineHeight: undefined,
    paddingHorizontal: 8,
  },
  inputContainer: {
    height: INPUT_HEIGHT_OLD,
    paddingVertical: 0,
  },
  inputLeftElement: {
    paddingLeft: 8,
    paddingVertical: 0,
  },
  inputRightElement: {
    paddingRight: 12,
    paddingVertical: 0,
  },
  topV2: {
    top: -INPUT_HEIGHT - 12,
  },
}));
